/**
 * The attachment rules the browser checks before uploading (TMMAPP-0142 item 25a; TMMAPI-0181 U7
 * item 42): images (PNG, JPEG, WebP), PDFs and spreadsheets (CSV, XLSX); at most 10 MB a file and 5 a
 * message. The session's 50 MB is the server's alone (TMMAPP-0148 U2: the browser no longer holds the
 * whole transcript). The server checks again, by content, and its answer wins
 * (`413 ASSISTANT_ATTACHMENT_TOO_LARGE {limit, limitKind}`, `415 ASSISTANT_ATTACHMENT_TYPE`).
 */
import type { TFunction } from 'i18next';
import type { AssistantAttachmentKind } from '@/api/assistant';
import { errorCopy } from '@/api/errorCopy';
import { backendNumber } from '@/api/errorDetails';
import { ErrorCodes, isApiError } from '@/api/errors';
import { formatByteSize, formatNumber } from '@/i18n/format';

const MEBIBYTE = 1024 * 1024;

export const ATTACHMENT_FILE_MAX_BYTES = 10 * MEBIBYTE;
export const ATTACHMENT_MESSAGE_MAX_FILES = 5;

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const KIND_BY_EXTENSION: Readonly<Record<string, AssistantAttachmentKind>> = {
  png: 'IMAGE',
  jpg: 'IMAGE',
  jpeg: 'IMAGE',
  webp: 'IMAGE',
  pdf: 'PDF',
  csv: 'SHEET',
  xlsx: 'SHEET',
};

const KIND_BY_TYPE: Readonly<Record<string, AssistantAttachmentKind>> = {
  'image/png': 'IMAGE',
  'image/jpeg': 'IMAGE',
  'image/webp': 'IMAGE',
  'application/pdf': 'PDF',
  'text/csv': 'SHEET',
  [XLSX_TYPE]: 'SHEET',
};

/** The file picker's filter: the accepted extensions and their types. */
export const ATTACHMENT_ACCEPT = [
  ...Object.keys(KIND_BY_EXTENSION).map((extension) => `.${extension}`),
  ...Object.keys(KIND_BY_TYPE),
].join(',');

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

/**
 * What a picked file looks like it is, by its type or else its name; null when it's none of the
 * accepted kinds. (The browser's type for a CSV varies by system, so the name also counts.)
 */
export function fileKind(file: Pick<File, 'name' | 'type'>): AssistantAttachmentKind | null {
  return KIND_BY_TYPE[file.type] ?? KIND_BY_EXTENSION[extensionOf(file.name)] ?? null;
}

const BANK_FILE_EXTENSIONS: ReadonlySet<string> = new Set(['csv', 'ofx', 'qfx']);

/** A bank export (CSV, OFX, QFX): attaching it doesn't import it, so the composer says where to. */
export function isBankFile(name: string): boolean {
  return BANK_FILE_EXTENSIONS.has(extensionOf(name));
}

/**
 * `413 ASSISTANT_ATTACHMENT_TOO_LARGE` worded by its `limitKind` (§G U5): a file's bytes, a
 * message's files or bytes, the session's bytes, a PDF's pages. The limit is the served one; an
 * unknown kind, or one without a limit, reads the generic sentence.
 */
export function attachmentErrorCopy(
  t: TFunction,
  error: unknown,
  locale: string,
  fallbackKey = 'assistant.attach.error.upload',
): string {
  if (!isApiError(error) || error.code !== ErrorCodes.ASSISTANT_ATTACHMENT_TOO_LARGE) {
    return errorCopy(t, error, fallbackKey);
  }
  const limit = backendNumber(error, 'limit');
  const backend = error.details.backend;
  const served = typeof backend === 'object' && backend !== null ? backend.limitKind : undefined;
  const kind = typeof served === 'string' ? served : null;
  if (limit === null || kind === null) return t('assistant.attach.error.tooLarge');
  switch (kind) {
    case 'FILE_BYTES':
    case 'IMAGE_BYTES':
    case 'UNPACKED_BYTES':
      return t('assistant.attach.limit.file', { limit: formatByteSize(limit, locale) });
    case 'MESSAGE_FILES':
      return t('assistant.attach.limit.message', { limit: formatNumber(limit, locale) });
    case 'MESSAGE_BYTES':
      return t('assistant.attach.limit.messageBytes', { limit: formatByteSize(limit, locale) });
    case 'SESSION_BYTES':
      return t('assistant.attach.limit.session', { limit: formatByteSize(limit, locale) });
    case 'PDF_PAGES':
    case 'MESSAGE_PAGES':
      return t('assistant.attach.limit.pages', { limit: formatNumber(limit, locale) });
    default:
      return t('assistant.attach.error.tooLarge');
  }
}
